import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
    faCheck,
    faStepBackward,
    faStepForward,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useState,
} from "react";
import { Breadcrumb, Tab } from "react-bootstrap";
import useWizard, { IUseWizardOptions } from "../../../hooks/useWizard";
import IconOrSpinner from "../IconOrSpinner";

interface IWizardProps<TStepKeys extends string> {
    children: React.ReactNode;
    blockBreadCrumb?: boolean;
    stepInfo: Record<
        TStepKeys,
        {
            title: string;
            valid?: boolean;
        }
    >;
    steps: TStepKeys[];
    changeStep: {
        goTo: (step: TStepKeys) => void;
        next: () => void;
        previous: () => void;
    };
    goTo: (step: TStepKeys) => void;
    step: TStepKeys;
    visiteds: TStepKeys[];
    unmountOnExit?: boolean;
}
interface IWizardStepProps<TStepKeys extends string> {
    stepKey: TStepKeys;
    children: React.ReactNode;
}
interface IWizardControlProps<TStepKeys extends string> {
    nextIcon?: IconProp;
    previousIcon?: IconProp;
    finishIcon?: IconProp;
    finishText?: string;
    buttonType?: "button" | "submit" | "reset";
    onClick?: () => void;
    children?: React.ReactNode;
    control?: Record<TStepKeys, boolean>;
}
interface IWizardControlStepProps<TStepKeys extends string> {
    step: TStepKeys;
    children: [React.ReactChild, React.ReactChild] | React.ReactChild;
    nextIcon?: IconProp;
    previousIcon?: IconProp;
    internal?: boolean;
}
interface IWizardControlStepButtonProps {
    children?: string;
    icon?: IconProp;
    disabled?: boolean;
    buttonType?: "button" | "submit" | "reset";
    onClick?: () => void;
    loading?: boolean;
}

export const createWizard = <TStepKeys extends string>(steps: TStepKeys[]) => {
    return {
        Wizard: (props: Omit<IWizardProps<TStepKeys>, "steps">) => (
            <Wizard<TStepKeys> {...props} steps={steps} />
        ),
        WizardStep: (props: IWizardStepProps<TStepKeys>) => (
            <WizardStep<TStepKeys> {...props} />
        ),
        useWizard: (options?: IUseWizardOptions<TStepKeys>) =>
            useWizard(steps, options),
        WizardControl: (props: IWizardControlProps<TStepKeys>) => (
            <WizardControl<TStepKeys> {...props} />
        ),
        WizardControlStep: (
            props: Exclude<IWizardControlStepProps<TStepKeys>, "internal">
        ) => <WizardControlStep {...props} internal={false} />,
        WizardControlStepButton,
    };
};

// contexts
const WizardComponentContext = createContext({
    next: () => {},
    previous: () => {},
    step: "",
    steps: [""],
});
const WizardControlContext = createContext({
    nextIcon: undefined as IconProp | undefined,
    previousIcon: undefined as IconProp | undefined,
    dispatchChildrenControl: (step: string) => {},
    buttonType: "button" as "button" | "submit" | "reset" | undefined,
    onClick: undefined as (() => void) | undefined,
});
const WizardControlStepContext = createContext("next" as "next" | "previous");

function Wizard<TStepKeys extends string>(props: IWizardProps<TStepKeys>) {
    const {
        step,
        steps,
        children,
        blockBreadCrumb,
        visiteds,
        goTo,
        stepInfo,
        changeStep,
    } = props;

    return (
        <WizardComponentContext.Provider
            value={{
                next: changeStep.next,
                previous: changeStep.previous,
                step,
                steps,
            }}
        >
            <Tab.Container
                id="left-tabs-example"
                defaultActiveKey="selecParcelas"
                activeKey={step}
                mountOnEnter
                unmountOnExit={props.unmountOnExit}
            >
                <Breadcrumb>
                    {visiteds.map((val) => {
                        const stepVal = val as TStepKeys;
                        return (
                            <Breadcrumb.Item
                                className="align-items-center"
                                active={stepVal === step || blockBreadCrumb}
                                key={stepVal}
                                onClick={() => {
                                    if (!blockBreadCrumb) goTo(stepVal);
                                }}
                            >
                                {stepInfo[stepVal].title}{" "}
                                {stepInfo[stepVal].valid === true ? (
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="ml-2 text-primary"
                                    />
                                ) : (
                                    stepInfo[stepVal].valid === false && (
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            className="ml-2 text-danger"
                                        />
                                    )
                                )}
                            </Breadcrumb.Item>
                        );
                    })}
                </Breadcrumb>
                <Tab.Content>{children}</Tab.Content>
            </Tab.Container>
        </WizardComponentContext.Provider>
    );
}

function WizardStep<TStepKeys extends string>(
    props: IWizardStepProps<TStepKeys>
) {
    return <Tab.Pane eventKey={props.stepKey}>{props.children}</Tab.Pane>;
}
function WizardControlReducer<TStepKeys extends string>(
    currentState: Record<TStepKeys, boolean>,
    action: TStepKeys
) {
    currentState[action] = true;
    return currentState;
}
function WizardControl<TStepKeys extends string>(
    props: IWizardControlProps<TStepKeys>
) {
    const { steps } = useContext(WizardComponentContext);
    const [childrenControl, dispatchChildrenControl] = useReducer(
        WizardControlReducer,
        props.control ?? {}
    );
    const [cc, setCC] = useState({} as Record<string, boolean>);
    useEffect(() => {
        setCC(childrenControl);
    }, [childrenControl]);

    return (
        <div className="w-100">
            <WizardControlContext.Provider
                value={{
                    nextIcon: props.nextIcon,
                    previousIcon: props.previousIcon,
                    dispatchChildrenControl,
                    buttonType: props.buttonType,
                    onClick: props.onClick,
                }}
            >
                {steps
                    .filter((step) => !cc[step])
                    .map((step) => {
                        const proximo = (
                            <WizardControlStepButton
                                children={
                                    steps.indexOf(step) === steps.length - 1
                                        ? props.finishText ?? "Finalizar"
                                        : undefined
                                }
                                icon={
                                    steps.indexOf(step) === steps.length - 1
                                        ? props.finishIcon ?? faCheck
                                        : undefined
                                }
                            />
                        );
                        return steps.indexOf(step) > 0 ? (
                            <WizardControlStep step={step} internal key={step}>
                                <WizardControlStepButton />
                                {proximo}
                            </WizardControlStep>
                        ) : (
                            <WizardControlStep step={step} internal key={step}>
                                {proximo}
                            </WizardControlStep>
                        );
                    })}

                {props.children}
            </WizardControlContext.Provider>
        </div>
    );
}
function WizardControlStep<TStepKeys extends string>(
    props: IWizardControlStepProps<TStepKeys>
) {
    const { step } = useContext(WizardComponentContext);
    const { dispatchChildrenControl } = useContext(WizardControlContext);

    const justify = Array.isArray(props.children) ? "between" : "end";
    const children = Array.isArray(props.children) ? (
        <>
            <WizardControlStepContext.Provider value={"previous"}>
                {props.children[0]}
            </WizardControlStepContext.Provider>
            <WizardControlStepContext.Provider value={"next"}>
                {props.children[1]}
            </WizardControlStepContext.Provider>
        </>
    ) : (
        <WizardControlStepContext.Provider value={"next"}>
            {props.children}
        </WizardControlStepContext.Provider>
    );

    useEffect(() => {
        if (!props.internal) dispatchChildrenControl(props.step);
    }, [dispatchChildrenControl, props.step, props.internal]);

    if (props.step === step)
        return (
            <div className={`d-flex justify-content-${justify} mt-5`}>
                {children}
            </div>
        );
    else return null;
}
function WizardControlStepButton(props: IWizardControlStepButtonProps) {
    const { next, previous } = useContext(WizardComponentContext);
    const {
        nextIcon,
        previousIcon,
        buttonType,
        onClick: contextOnClick,
    } = useContext(WizardControlContext);
    const action = useContext(WizardControlStepContext);
    const icon =
        props.icon ??
        (action === "next"
            ? nextIcon ?? faStepForward
            : previousIcon ?? faStepBackward);
    const text = props.children ?? (action === "next" ? "Próximo" : "Voltar");

    function onClick() {
        if (action === "next") next();
        else previous();

        if (props.onClick) props.onClick();
        else if (contextOnClick) contextOnClick();
    }

    return (
        <button
            disabled={props.disabled}
            className="btn btn-primary d-flex align-items-center g-2"
            type={props.buttonType ?? buttonType ?? "button"}
            onClick={onClick}
        >
            {action === "previous" && (
                <IconOrSpinner loading={!!props.loading} icon={icon} />
            )}
            {text}
            {action === "next" && (
                <IconOrSpinner loading={!!props.loading} icon={icon} />
            )}
        </button>
    );
}
